import * as React from "react"

// eslint-disable-next-line no-restricted-imports
import Done from "@mui/icons-material/Done"
// eslint-disable-next-line no-restricted-imports
import FileCopyOutlined from "@mui/icons-material/FileCopyOutlined"
import Button from "@mui/material/Button"
import TextField, { TextFieldProps } from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { styled, useTheme } from "@mui/material/styles"

import { Locale, useTranslation } from "../utils"
import locales from "./locales"

interface CopyAreaProps {
    TextAreaProps?: TextFieldProps
    value: {
        header: string
        lines: string[]
        delimiter?: string
    }
    locale?: Locale
    label: string
}

const CopyArea: React.FC<CopyAreaProps> = ({
    TextAreaProps: textAreaProps,
    value,
    label
}) => {
    const [copied, setCopied] = React.useState(false)

    const copyValue = [
        value.header,
        value.lines
            .map(line => {
                // if the line starts with \n do not use any delimiters
                if (line.search(/\n/) === 0) return line

                return `${value.delimiter ?? "- "}${line}`
            })
            .join("\n")
    ].join("\n")

    const handleCopy = async () => {
        if ("clipboard" in navigator)
            await navigator.clipboard.writeText(copyValue)
        //Remove when we drop IE11 support
        else if ("clipboardData" in window)
            //@ts-ignore
            window.clipboardData.setData("Text", copyValue)

        setCopied(true)
    }

    React.useEffect(() => {
        const handle = setTimeout(() => {
            if (copied) setCopied(false)
        }, 1000)
        return () => clearTimeout(handle)
    }, [copied])

    React.useEffect(() => {
        setCopied(false)
    }, [copyValue])

    const [t] = useTranslation({ locales })
    const theme = useTheme()
    return (
        <div style={{ paddingTop: theme.spacing(3) }}>
            <Typography
                variant="h3"
                component="label">
                {label}
                <div
                    style={{
                        position: "relative",
                        marginTop: theme.spacing(),
                        minHeight: "50%"
                    }}>
                    <StyledTextField
                        multiline
                        fullWidth
                        maxRows={value.lines.length + 2}
                        disabled
                        {...textAreaProps}
                        value={copyValue}
                    />
                    <Button
                        style={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            margin: theme.spacing()
                        }}
                        variant="contained"
                        disabled={!value.lines.length || copied}
                        startIcon={copied ? <Done /> : <FileCopyOutlined />}
                        color="primary"
                        onClick={handleCopy}
                        size="small">
                        {t(copied ? "copied" : "copy")}
                    </Button>
                </div>
            </Typography>
        </div>
    )
}

const StyledTextField = styled(TextField)(({ theme }) => ({
    "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: theme.palette.text.primary
    }
}))

export default CopyArea
